<template>
	<v-card flat class="telephony-panel">
		<!-- Tab Navigation -->
		<div class="tab-bar">
			<button
				v-for="tab in tabs"
				:key="tab.id"
				class="tab-btn"
				:class="{ active: activeTab === tab.id }"
				:style="activeTab === tab.id ? { '--active-color': tab.color, '--active-bg': tab.bg } : {}"
				@click="activeTab = tab.id"
			>
				<v-icon small :color="activeTab === tab.id ? tab.color : 'grey'" class="tab-icon">{{ tab.icon }}</v-icon>
				<span class="tab-label" :class="{ 'hidden-xs-only': activeTab !== tab.id }">{{ tab.label }}</span>
				<v-progress-circular
					v-if="tabLoading[tab.id]"
					:size="14"
					:width="2"
					indeterminate
					:color="activeTab === tab.id ? tab.color : '#9e9e9e'"
					class="tab-spinner"
				/>
				<span v-if="activeTab === tab.id" class="tab-indicator" :style="{ background: tab.color }" />
			</button>
		</div>

		<!-- Tab Content -->
		<div class="tab-content">
			<!-- SIP Users Tab -->
			<div v-show="activeTab === 'sip'" class="tab-pane">
				<sip-panel
					ref="sipPanel"
					@update:loading="val => tabLoading.sip = val"
				/>
			</div>

			<!-- Yealink Devices Tab -->
			<div v-show="activeTab === 'devices'" class="tab-pane tab-pane-flex">
				<yealink-panel
					ref="yealinkPanel"
					embedded
					@update:loading="val => tabLoading.devices = val"
					@configure-device="configureDevice"
				/>
			</div>

			<!-- Yealink Config Tab -->
			<div v-show="activeTab === 'config'" class="tab-pane">
				<yealink-config-panel
					ref="yealinkConfigPanel"
					embedded
					@update:loading="val => tabLoading.config = val"
					@open-sip-panel="activeTab = 'sip'"
					@open-yealink-panel="activeTab = 'devices'"
				/>
			</div>
		</div>
	</v-card>
</template>

<script>
import SipPanel from "@/components/support/SipPanel.vue";
import YealinkPanel from "@/components/support/YealinkPanel.vue";
import YealinkConfigPanel from "@/components/support/YealinkConfigPanel.vue";

export default {
	name: "TelephonyPanel",

	components: {
		SipPanel,
		YealinkPanel,
		YealinkConfigPanel
	},

	data() {
		return {
			activeTab: "sip",
			tabLoading: {
				sip: false,
				devices: false,
				config: false
			}
		};
	},

	computed: {
		tabs() {
			return [
				{
					id: "sip",
					icon: "mdi-phone-voip",
					label: this.$t("sip-management"),
					color: "#1976d2",
					bg: "rgba(25, 118, 210, 0.08)"
				},
				{
					id: "devices",
					icon: "mdi-deskphone",
					label: this.$t("yealink-panel"),
					color: "#00acc1",
					bg: "rgba(0, 172, 193, 0.08)"
				},
				{
					id: "config",
					icon: "mdi-file-cog",
					label: this.$t("yealink-config-panel"),
					color: "#00bfa5",
					bg: "rgba(0, 191, 165, 0.08)"
				}
			];
		}
	},

	mounted() {
	},

	beforeDestroy() {
	},

	methods: {
		/** Exposed for parent to call refresh on all panels */
		refresh() {
			return Promise.allSettled([
				this.$refs.sipPanel?.getSipUsers?.(),
				this.$refs.yealinkPanel?.fetchDevices?.(),
				this.$refs.yealinkConfigPanel?.fetchConfigData?.()
			]);
		},

		async silentRefreshAll() {
			try {
				const results = await Promise.allSettled([
					this.$refs.sipPanel?.getSipUsers?.({ silent: true }),
					this.$refs.yealinkPanel?.fetchDevices?.({ silent: true }),
					this.$refs.yealinkConfigPanel?.fetchConfigData?.({ silent: true })
				]);

				const hasError = results.some(r =>
					r.status === "rejected" || (r.status === "fulfilled" && r.value?.success === false)
				);

				return { success: !hasError };
			} catch {
				return { success: false };
			}
		},

		/** Exposed for parent to reset config form */
		resetConfigForm() {
			this.$refs.yealinkConfigPanel?.resetForm?.();
		},

		configureDevice(mac) {
			this.$refs.yealinkConfigPanel?.resetForm?.();
			this.activeTab = "config";
			this.$nextTick(() => {
				const panel = this.$refs.yealinkConfigPanel;

				if (panel) {
					panel.macAddress = mac;
					panel.onMacAddressChange(mac);
				}
			});
		}
	}
};
</script>

<style scoped>
	.telephony-panel {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	/* ── Tab Bar ────────────────────────────────────────── */
	.tab-bar {
		display: flex;
		gap: 2px;
		padding: 6px 10px 0;
		background: #fafafa;
		border-bottom: 1px solid #e0e0e0;
		flex-shrink: 0;
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	.tab-btn {
		position: relative;
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 10px 16px 12px;
		border: none;
		background: transparent;
		cursor: pointer;
		border-radius: 8px 8px 0 0;
		transition: all 0.2s ease;
		white-space: nowrap;
		flex-shrink: 0;
	}

	.tab-btn:hover {
		background: rgba(0, 0, 0, 0.04);
	}

	.tab-btn.active {
		background: var(--active-bg, rgba(25, 118, 210, 0.08));
	}

	.tab-label {
		font-size: 13px;
		font-weight: 500;
		color: #757575;
		transition: color 0.2s ease;
	}

	.tab-btn.active .tab-label {
		color: var(--active-color, #1976d2);
		font-weight: 600;
	}

	.tab-indicator {
		position: absolute;
		bottom: 0;
		left: 8px;
		right: 8px;
		height: 3px;
		border-radius: 3px 3px 0 0;
		transition: all 0.2s ease;
	}

	.tab-icon {
		flex-shrink: 0;
	}

	.tab-spinner {
		flex-shrink: 0;
		margin-left: 2px;
	}

	/* ── Tab Content ────────────────────────────────────── */
	.tab-content {
		flex: 1;
		min-height: 0;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.tab-pane {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		overflow-x: hidden;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
	}

	.tab-pane-flex {
		display: flex;
		flex-direction: column;
	}

	/* Remove margin from inner SIP card since we already have panel spacing */
	.tab-pane >>> .v-card {
		margin: 0 !important;
	}

	/* ── Mobile (xs) ────────────────────────────────────── */
	@media (max-width: 599px) {
		.tab-bar {
			padding: 4px 6px 0;
			gap: 0;
		}

		.tab-btn {
			padding: 8px 10px 10px;
			gap: 4px;
			flex: 1;
			justify-content: center;
			min-width: 0;
		}

		.tab-label {
			font-size: 11px;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		/* On xs, only show the label of the active tab to save space */
		.tab-btn:not(.active) .tab-label {
			display: none;
		}

		.tab-btn:not(.active) {
			flex: 0 0 auto;
			padding: 8px 14px 10px;
		}
	}

	/* ── Custom scrollbar ──────────────────────────────── */
	.tab-pane::-webkit-scrollbar {
		width: 5px;
	}

	.tab-pane::-webkit-scrollbar-track {
		background: transparent;
	}

	.tab-pane::-webkit-scrollbar-thumb {
		background: #ccc;
		border-radius: 3px;
	}

	.tab-pane::-webkit-scrollbar-thumb:hover {
		background: #aaa;
	}
</style>
